import { useEffect, useState } from "react";
import { getTracking } from "../lib/api";

/**
 * 📈 推廣追蹤（任務 G3，spec 52408d1；照 Marveland 來源×漏斗 pattern）
 * 歸因＝每 session 首則 user 訊息前綴（SHOW1-3／FB1-4／IG／WEB／BD1-5／STATUS-），
 * 無前綴＝自然/口碑。漏斗：對話→訊息→handoff→reminder→reminder 後轉真人。
 * 另附 referral 短連結點擊（v36 渠道口徑，與前綴歸因並存）。
 */

interface TrackingRow {
  source: string;
  sessions: number;
  msgs: number;
  handoffs: number;
  reminders: number;
}

export default function TrackingPage() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState<{
    days: number;
    referralClicks: number;
    reminderThenHandoff: number;
    rows: TrackingRow[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    setError("");
    getTracking(days)
      .then((d) => {
        if (d && Array.isArray(d.rows)) setData(d);
        else setError(d?.error || "載入失敗");
      })
      .catch((err) => setError(err instanceof Error ? err.message : "載入失敗"))
      .finally(() => setLoading(false));
  }, [days]);

  const totals = data?.rows
    ? data.rows.reduce(
        (acc, r) => ({
          sessions: acc.sessions + r.sessions,
          msgs: acc.msgs + r.msgs,
          handoffs: acc.handoffs + r.handoffs,
          reminders: acc.reminders + r.reminders,
        }),
        { sessions: 0, msgs: 0, handoffs: 0, reminders: 0 }
      )
    : { sessions: 0, msgs: 0, handoffs: 0, reminders: 0 };

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-100 mb-2">📈 推廣追蹤</h1>
      <p className="text-xs text-slate-500 mb-6">
        歸因＝對話首則訊息前綴（SHOW1-3／FB1-4／IG／WEB／BD1-5／STATUS-）；無前綴＝自然/口碑。數據源：chat_logs＋handoff_events＋feature_usage。
      </p>

      <div className="flex items-center gap-3 mb-6">
        <div className="flex rounded-lg border border-slate-700 overflow-hidden">
          {[7, 30].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                days === d
                  ? "bg-emerald-600/20 text-emerald-300 border-emerald-500"
                  : "bg-slate-950/40 text-slate-400 hover:text-slate-200 border-slate-700/50"
              }`}
            >
              近 {d} 日
            </button>
          ))}
        </div>
        {data && (
          <div className="flex gap-4 text-xs text-slate-400">
            <span>
              總對話 <b className="text-slate-200">{totals.sessions}</b>
            </span>
            <span>
              訊息 <b className="text-slate-200">{totals.msgs}</b>
            </span>
            <span>
              轉真人 <b className="text-slate-200">{totals.handoffs}</b>
            </span>
            <span>
              2h 提醒 <b className="text-slate-200">{totals.reminders}</b>（其後轉真人{" "}
              <b className="text-slate-200">{data.reminderThenHandoff}</b>）
            </span>
            <span>
              Referral 短連結點擊 <b className="text-slate-200">{data.referralClicks}</b>
            </span>
          </div>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">載入中…</p>
      ) : error ? (
        <p className="text-sm text-rose-400">{error}</p>
      ) : (
        <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-950/60 text-slate-400 text-xs uppercase tracking-wider">
                <th className="px-4 py-3 text-left">來源</th>
                <th className="px-4 py-3 text-right">對話</th>
                <th className="px-4 py-3 text-right">訊息</th>
                <th className="px-4 py-3 text-right">轉真人</th>
                <th className="px-4 py-3 text-right">2h 提醒</th>
                <th className="px-4 py-3 text-right">轉化率</th>
              </tr>
            </thead>
            <tbody>
              {data!.rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    此期間無對話數據——投放後首則訊息帶前綴（如 SHOW1-你好）即會入榜
                  </td>
                </tr>
              ) : (
                data!.rows.map((r) => (
                  <tr key={r.source} className="border-t border-slate-800/60 hover:bg-slate-800/30">
                    <td className="px-4 py-2.5 text-slate-200 font-medium">{r.source}</td>
                    <td className="px-4 py-2.5 text-right text-slate-300">{r.sessions}</td>
                    <td className="px-4 py-2.5 text-right text-slate-300">{r.msgs}</td>
                    <td className="px-4 py-2.5 text-right text-slate-300">{r.handoffs}</td>
                    <td className="px-4 py-2.5 text-right text-slate-300">{r.reminders}</td>
                    <td className="px-4 py-2.5 text-right">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          r.handoffs > 0
                            ? "bg-emerald-500/15 text-emerald-300"
                            : "bg-slate-500/10 text-slate-400"
                        }`}
                      >
                        {r.sessions > 0 ? Math.round((r.handoffs / r.sessions) * 100) : 0}%
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
